import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Item, ItemImage } from '@lr/shared';
import { apiFetch } from '../../lib/api';
import { downloadCsv, parseCsv, stringifyCsv } from '../../lib/csv';
import { StatusBadge } from './AdminDashboard';

type AdminItemRow = Item & {
  brand?: { name: string; slug: string } | null;
  category?: { name: string; slug: string } | null;
  images?: ItemImage[] | null;
};

type StatusFilter = 'all' | 'available' | 'draft' | 'coming_soon' | 'archived';

// Header order = column order in the exported template. Keep it stable.
const CSV_HEADERS = [
  'id',
  'slug',
  'name',
  'description',
  'brand_slug',
  'category_slug',
  'gender',
  'condition',
  'credit_price',
  'label_size',
  'recommended_size',
  'pit_to_pit_cm',
  'shoulder_width_cm',
  'length_cm',
  'damage_fee_pounds',
  'main_image_url',
  'gallery_image_urls',
  'colours',
  'status'
];

interface ImportResult {
  created: number;
  updated: number;
  errors: { row: number; slug?: string; error: string }[];
}

export function AdminItems() {
  const navigate = useNavigate();
  const [items, setItems] = useState<AdminItemRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function toggleFeatured(it: AdminItemRow) {
    // Optimistic: flip locally, then PATCH. If it fails, refetch to roll
    // back. Featured pins the item to the top of Browse.
    const next = !it.featured_at;
    setItems((rows) =>
      rows.map((r) =>
        r.id === it.id
          ? { ...r, featured_at: next ? new Date().toISOString() : null }
          : r
      )
    );
    try {
      await apiFetch(`/api/admin/items/${it.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ featured: next })
      });
    } catch {
      load();
    }
  }

  function load() {
    setLoading(true);
    apiFetch<AdminItemRow[]>('/api/admin/items')
      .then(setItems)
      .finally(() => setLoading(false));
  }

  function handleExport() {
    const rows = items.map((i) => ({
      id: i.id,
      slug: i.slug,
      name: i.name,
      description: i.description ?? '',
      brand_slug: i.brand?.slug ?? '',
      category_slug: i.category?.slug ?? '',
      gender: i.gender,
      condition: i.condition,
      credit_price: i.credit_price,
      label_size: i.label_size ?? '',
      recommended_size: i.recommended_size ?? '',
      pit_to_pit_cm: i.pit_to_pit_cm ?? '',
      shoulder_width_cm: i.shoulder_width_cm ?? '',
      length_cm: i.length_cm ?? '',
      damage_fee_pounds: (i.damage_fee_pence / 100).toFixed(2),
      main_image_url: i.main_image_url ?? '',
      gallery_image_urls: (i.images ?? [])
        .slice()
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((img) => img.url)
        .join(';'),
      colours: (i.colours ?? []).join(';'),
      status: i.status
    }));
    const csv = stringifyCsv(rows as unknown as Record<string, unknown>[], CSV_HEADERS);
    const date = new Date().toISOString().slice(0, 10);
    downloadCsv(csv, `label-rotation-items-${date}.csv`);
  }

  function handleImportClick() {
    setImportError(null);
    setImportResult(null);
    fileInputRef.current?.click();
  }

  async function handleFileChosen(file: File) {
    setImportError(null);
    setImportResult(null);
    setImporting(true);
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (rows.length === 0) {
        throw new Error('No data rows found. The first line must be the header.');
      }
      const result = await apiFetch<ImportResult>('/api/admin/items/import', {
        method: 'POST',
        body: JSON.stringify({ rows })
      });
      setImportResult(result);
      load();
    } catch (e) {
      setImportError(e instanceof Error ? e.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  }

  useEffect(load, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i) => {
      if (filter !== 'all' && i.status !== filter) return false;
      if (!q) return true;
      return (
        i.name.toLowerCase().includes(q) ||
        i.slug.toLowerCase().includes(q) ||
        (i.brand?.name ?? '').toLowerCase().includes(q) ||
        (i.category?.name ?? '').toLowerCase().includes(q)
      );
    });
  }, [items, search, filter]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: items.length };
    for (const it of items) c[it.status] = (c[it.status] ?? 0) + 1;
    return c;
  }, [items]);

  const filters: { key: StatusFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'available', label: 'Live' },
    { key: 'draft', label: 'Draft' },
    { key: 'coming_soon', label: 'Coming soon' },
    { key: 'archived', label: 'Archived' }
  ];

  return (
    <>
      <div className="admin-page-head">
        <div>
          <h1>Items</h1>
          <div className="sub">{items.length} {items.length === 1 ? 'piece' : 'pieces'} in the rotation.</div>
        </div>
        <div className="actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={handleExport}
            disabled={items.length === 0}
            title={
              items.length === 0
                ? 'No items yet — add one first or import a CSV'
                : 'Download all items as CSV'
            }
          >
            ↓ Export CSV
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={handleImportClick}
            disabled={importing}
          >
            {importing ? 'Importing…' : '↑ Import CSV'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleFileChosen(f);
              e.target.value = '';
            }}
          />
          <Link to="new" className="btn-primary">
            + Add item
          </Link>
        </div>
      </div>

      {importError && <div className="a-alert a-alert-error">{importError}</div>}
      {importResult && (
        <div
          className={`a-alert ${
            importResult.errors.length === 0 ? 'a-alert-good' : 'a-alert-info'
          }`}
        >
          <strong>
            Import complete: {importResult.created} created, {importResult.updated} updated
            {importResult.errors.length > 0
              ? `, ${importResult.errors.length} skipped`
              : ''}
            .
          </strong>
          {importResult.errors.length > 0 && (
            <details style={{ marginTop: 8 }}>
              <summary style={{ cursor: 'pointer' }}>
                Show {importResult.errors.length} error
                {importResult.errors.length === 1 ? '' : 's'}
              </summary>
              <ul style={{ margin: '8px 0 0 16px', fontSize: 12 }}>
                {importResult.errors.map((e, i) => (
                  <li key={i}>
                    Row {e.row}
                    {e.slug ? ` (${e.slug})` : ''}: {e.error}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      <div className="a-card">
        <div className="a-toolbar">
          <div className="a-search">
            <span className="ic">
              <SearchIcon />
            </span>
            <input
              placeholder="Search by name, brand, slug…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {filters.map((f) => (
            <button
              key={f.key}
              className={`a-filter${filter === f.key ? ' active' : ''}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
              {counts[f.key] != null && (
                <span style={{ opacity: 0.7, marginLeft: 4 }}>{counts[f.key]}</span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="a-empty">
            <div className="p">Loading items…</div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="a-empty">
            <div className="h">{items.length === 0 ? 'No items yet' : 'No matches'}</div>
            <div className="p">
              {items.length === 0
                ? 'Create your first piece to start building the rotation.'
                : 'Try clearing search or selecting a different status.'}
            </div>
            {items.length === 0 && (
              <Link to="new" className="btn-primary">
                + Add item
              </Link>
            )}
          </div>
        ) : (
          <div className="a-table-wrap">
            <table className="a-table">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>Image</th>
                  <th>Name</th>
                  <th>Brand</th>
                  <th>Category</th>
                  <th>Size</th>
                  <th>Credits</th>
                  <th>Status</th>
                  <th style={{ width: 110 }}>Featured</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((i) => (
                  <tr
                    key={i.id}
                    className="clickable"
                    onClick={() => navigate(i.id)}
                  >
                    <td>
                      <div
                        className="thumb"
                        style={
                          i.main_image_url ? { backgroundImage: `url(${i.main_image_url})` } : undefined
                        }
                      />
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>
                        {i.name}
                        {i.featured_at && (
                          <span
                            style={{
                              marginLeft: 6,
                              fontSize: 10,
                              padding: '2px 6px',
                              borderRadius: 999,
                              background: 'var(--a-ink, #0e0e0e)',
                              color: '#fff',
                              fontWeight: 700,
                              letterSpacing: '0.08em',
                              verticalAlign: 'middle'
                            }}
                          >
                            FEATURED
                          </span>
                        )}
                      </div>
                      <div className="id">{i.slug}</div>
                    </td>
                    <td>{i.brand?.name ?? '—'}</td>
                    <td>{i.category?.name ?? '—'}</td>
                    <td>{i.label_size ?? '—'}</td>
                    <td className="num">{i.credit_price}</td>
                    <td>
                      <StatusBadge status={i.status} />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn-ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          void toggleFeatured(i);
                        }}
                        style={{
                          fontSize: 11,
                          padding: '4px 10px',
                          color: i.featured_at
                            ? 'var(--a-good, #1f7a3d)'
                            : 'var(--a-ink-soft)'
                        }}
                      >
                        {i.featured_at ? '★ Featured' : '☆ Feature'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </svg>
  );
}
